import React, { Component } from 'react'
import '../../styles/css/SideMenu.css'
import '../../styles/css/AdminMain.css'
import Swal from 'sweetalert2';
import {app, appDatabasePrimaryFunctions} from '../../base';
import logoWithTextImage from '../../styles/images/sqwad_logo_white.svg';
import { buildNavGroups } from '../utils/adminNav';
import { liveFeatures, namesOf } from '../utils/featureReachability';
import {emailKey} from '../utils/emailKey';
import { unsavedWork } from '../utils/unsavedChanges';

class SideMenu extends Component {
    constructor(props) {
        super(props);
        this.logOut = this.logOut.bind(this);
        this.toggleNav = this.toggleNav.bind(this);
        this.closeNav = this.closeNav.bind(this);
        this.state = {
            isGod: false,
            bottomTabsEnabled: false,
            // Which features this club has switched on. Empty until they load,
            // so a feature's screen appears rather than flickers away — the
            // nav renders once with the safe (smaller) set.
            features: {},
            navOpen: false
        }
    }

    // Signing out swaps the screen for the sign-in page without unloading
    // it, so an editor's beforeunload guard never fires and its unsaved work
    // simply vanishes. Editors that hold such work register it (see
    // utils/unsavedChanges), and this asks before throwing it away.
    async logOut() {
      const unsaved = unsavedWork();
      if (unsaved.length > 0) {
        const result = await Swal.fire({
          title: 'Log out without saving?',
          text: `You have unsaved changes to ${namesOf(unsaved.map(label => ({ label })))}. Logging out now loses them.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Log out anyway',
          cancelButtonText: 'Stay'
        });
        if (!result || !result.value) return;
      }
      app.auth().signOut().then((user, error) => {
        this.setState({ redirect: true })
      });
    }

    toggleNav() {
        this.setState(prev => ({ navOpen: !prev.navOpen }));
    }

    closeNav() {
        this.setState({ navOpen: false });
    }

    async componentDidMount() {
        const cleanEmail = emailKey(app.auth().currentUser.email);
        let isGod;
        try {
            const isGodRef = await appDatabasePrimaryFunctions.ref(`whitelist/${cleanEmail}`).once('value');
            isGod = isGodRef.exists();
        } catch (e) {
            console.log(e)
            isGod = false
        }

        // Check if bottom tabs are enabled
        let bottomTabsEnabled = false;
        try {
            const bottomTabsRef = await appDatabasePrimaryFunctions.ref('settings/bottomTabs/enabled').once('value');
            bottomTabsEnabled = bottomTabsRef.val() === true;
        } catch (e) {
            console.log(e);
        }

        // Any experience enabling bottom tabs should also surface the admin link
        if (!bottomTabsEnabled) {
            try {
                const experiencesSnap = await appDatabasePrimaryFunctions.ref('experiences').once('value');
                const experiences = experiencesSnap.val() || {};
                bottomTabsEnabled = Object.values(experiences).some(exp =>
                    exp && exp.settings && exp.settings.bottomTabs && exp.settings.bottomTabs.enabled === true);
            } catch (e) {
                console.log(e);
            }
        }

        this.setState({
            isGod: isGod,
            bottomTabsEnabled: bottomTabsEnabled
        });

        // Which screens this club's own feature switches ask for.
        //
        // SUBSCRIBED, not read once. These flags are changed from Feature
        // Access, which is a screen inside this very nav — so a one-shot read
        // meant switching a feature on left the nav it belongs to stale until
        // the page was reloaded. An operator turned something on, looked at the
        // sidebar, and concluded it had not worked.
        //
        // Listening also covers the case a reload never would: another admin,
        // or the same one in a second tab, switching something while this page
        // is open.
        //
        // A failure means the feature screens stay hidden, never that the nav
        // breaks — so both handlers land on an empty set rather than throwing.
        const onFeatureData = () => {
            try {
                this.setState({
                    features: liveFeatures({
                        flags: this.flagsValue || {},
                        access: this.accessValue || {}
                    })
                });
            } catch (e) {
                console.log(e);
                this.setState({ features: {} });
            }
        };
        // Whether the club HAS experiences, which is not the same question as
        // whether fans may switch between them — see adminNav.
        this.experiencesRef = appDatabasePrimaryFunctions.ref('experiences');
        this.experiencesListener = this.experiencesRef.on('value',
            (snap) => this.setState({ hasExperiences: snap.exists() && snap.numChildren() > 0 }),
            (e) => { console.log(e); this.setState({ hasExperiences: false }); });
        this.flagsRef = appDatabasePrimaryFunctions.ref('featureFlags');
        this.accessRef = appDatabasePrimaryFunctions.ref('featureAccess');
        this.flagsListener = this.flagsRef.on('value',
            (snap) => { this.flagsValue = snap.val(); onFeatureData(); },
            (e) => { console.log(e); this.setState({ features: {} }); });
        this.accessListener = this.accessRef.on('value',
            (snap) => { this.accessValue = snap.val(); onFeatureData(); },
            (e) => { console.log(e); this.setState({ features: {} }); });
    }

    componentWillUnmount() {
        if (this.flagsRef && this.flagsListener) this.flagsRef.off('value', this.flagsListener);
        if (this.accessRef && this.accessListener) this.accessRef.off('value', this.accessListener);
        if (this.experiencesRef && this.experiencesListener) this.experiencesRef.off('value', this.experiencesListener);
    }

    render() {
        // The gating lives in adminNav so it can be tested without rendering
        // this component (it reads auth and RTDB on mount).
        const groups = buildNavGroups(process.env, {
            isGod: this.state.isGod,
            bottomTabsEnabled: this.state.bottomTabsEnabled,
            features: this.state.features,
            hasExperiences: this.state.hasExperiences
        });
        const homeLink = "/admin";

        // Nothing ever set the `active` class, so the rail never showed you
        // where you were — the CSS for it has been sitting unused.
        const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
        const isActive = (item) => !item.external && item.href && path.toLowerCase() === item.href.toLowerCase();

        const renderItem = (item) => (
            <li key={item.title} className={isActive(item) ? 'active' : ''}>
                <a
                    href={item.href}
                    {...(item.external ? { target: '_blank', rel: 'noopener' } : {})}
                    {...(isActive(item) ? { 'aria-current': 'page' } : {})}
                >
                    <span className={`fa ${item.icon} pre_side_item`}/>
                    <span>{item.title}</span>
                </a>
            </li>
        );

        const navOpen = this.state.navOpen;

        return (
          <>
            <button
                type="button"
                className={`admin-nav-burger${navOpen ? ' is-hidden' : ''}`}
                onClick={this.toggleNav}
                aria-label="Open navigation"
                aria-expanded={navOpen}
            >
                <span className="fa fa-bars"/>
            </button>

            <button
                type="button"
                className={`admin-nav-scrim${navOpen ? ' is-open' : ''}`}
                onClick={this.closeNav}
                aria-label="Close navigation"
                tabIndex={navOpen ? 0 : -1}
            />

            <div className={`admin-sidebar${navOpen ? ' is-open' : ''}`}>
              <div className="logo">
                <a href={homeLink}>
                  <div className="logo-img"><img src={logoWithTextImage} height="30" alt="SQWAD"/></div>
                </a>
              </div>
              <div className="nav-container">
                {groups.map(group => {
                    const visible = group.items.filter(item => item.show);
                    if (visible.length === 0) return null;
                    return (
                        <div key={group.label}>
                            <span className="nav-group-label">{group.label}</span>
                            <ul className="nav">
                                {visible.map(renderItem)}
                            </ul>
                        </div>
                    );
                })}
                <ul className="nav nav-logout">
                  <li>
                    <button type="button" className="nav-logout-btn" onClick={this.logOut}>
                      <span className="fa fa-sign-out pre_side_item"/>
                      <span>Log out</span>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </>
        );
    }
}

export default SideMenu
